<template>
  <NuxtLayout name="default" :breadcrumbs="breadcrumbs">
    <CategoryPageContent
      v-if="productsCatalog"
      :title="$t('allProducts')"
      :totalProducts="totalProductsCount"
      :products="productsCatalog"
      :currentPage="currentPage"
      :subCategories="categories"
      :description="categoryData?.description ?? '' "
    >
      <template #sidebar>
        <CategorySearch :filteredFacts="filteredFacts"/>
        <div class="mt-6">
          <CategorySorting />

          <FilterGroup v-if="filteredFacts.length" v-for="facet in filteredFacts" :key="facet.attribute_code" :title="facet.label">
            <FilterCheckbox
              v-for="option in facet?.options"
              :key="option.value"
              v-model="facet.selected"
              :label="option.label"
              :value="option.value"
              :attribute_code="facet.attribute_code"
            />
          </FilterGroup>
        </div>
      </template>
    </CategoryPageContent>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { Breadcrumb } from '~/components/ui/Breadcrumbs/types';
import FilterGroup from "~/components/FilterSidebar/FilterGroup.vue";
import FilterCheckbox from "~/components/FilterSidebar/FilterCheckbox.vue";
import { ref, computed, watch, onMounted } from 'vue';

definePageMeta({
  layout: false,
});

const route = useRoute();
const router = useRouter();

// Make these reactive so they update when route changes
const currentPage = computed(() => Number(route.query.page) || 1);
const sortQuery = computed(() => route.query.sort || "");

// Make sort reactive based on sortQuery
const sort = computed(() => {
  switch (sortQuery.value) {
    case 'price-low-to-high':
      return {'price': "ASC"}
    case 'price-high-to-low':
      return {'price': "DESC"}
    case 'name-az':
      return {'name': "ASC"}
    case 'name-za':
      return {'name': "DESC"}
    default:
      return {'price': "ASC"};
  }
});

const { getCategoryData, getCategoryFacets, getProductsWithFilter } = useCategory();
const { t } = useI18n();

// Make products reactive
const products = ref([]);
const totalProductsCount = ref(0); // Add reactive total count
const loading = ref(false);
const categoryData = ref(null);
const categoryFacts = ref([]);

// Function to build filters from current route query
function buildFiltersFromQuery(query: Record<string, any>, categoryId) {
  const filters: Record<string, any> = {}
  for (const [key, value] of Object.entries(query)) {
    if (key === 'page' || key === 'sort' || key === 'slug') {
      continue;
    }

    if (key === 'price' && typeof value === 'string' && value.includes('_')) {
      const [from, to] = value.split('_')
      filters[key] = { from, to }
      continue
    }
    filters[key] = { eq: value }
  }

  if(filters && Object.keys(filters).length > 0) {
    filters["category_id"] = { eq: categoryId }
  }

  return filters
}

// Function to check if there are filters in the URL
const hasFiltersInUrl = computed(() => {
  const filterKeys = Object.keys(route.query).filter(key => 
    key !== 'page' && key !== 'sort' && key !== 'slug'
  );
  return filterKeys.length > 0;
});

// Initialize category data - load basic category info first
categoryData.value = await getCategoryData(route.params.slug as string, 1, {'price': "ASC"});
categoryFacts.value = await getCategoryFacets(categoryData.value?.id);

// Initialize total count from category data
totalProductsCount.value = categoryData.value?.products?.total_count || 0;

const filteredFacts = computed(() =>
  (categoryFacts.value?.length ? categoryFacts.value.filter(f => f?.attribute_code !== 'category_uid') : [])
);

const breadcrumbs: Breadcrumb[] = [
  { name: t('home'), link: '/' },
  { name: t('allCategories'), link: `${paths.categories}` },
];

const subCategories = computed(() => categoryData.value?.children);
const categories = computed(
  () =>
    subCategories.value?.map(({ name, product_count, url_key }) => ({
      name,
      count: product_count || undefined,
      href: `${paths.category}/${url_key}`,
    })) || [],
);

// Function to load products based on current filters and sorting
async function loadProducts() {
  try {
    loading.value = true;
    const filters = buildFiltersFromQuery(route.query, categoryData.value?.id);

    if(filters && Object.keys(filters).length > 0) {
      // Load filtered products
      const filteredProductsResponse = await getProductsWithFilter(
        currentPage.value,
        sort.value,
        filters,
      );

      // Handle different response formats and extract total count
      if (filteredProductsResponse?.data) {
        products.value = filteredProductsResponse.data.items || filteredProductsResponse.data;
        totalProductsCount.value = filteredProductsResponse.data.total_count || 
                                  filteredProductsResponse.data.page_info?.total || 
                                  (Array.isArray(filteredProductsResponse.data) ? filteredProductsResponse.data.length : 0);
      } else if (filteredProductsResponse?.items) {
        products.value = filteredProductsResponse.items;
        totalProductsCount.value = filteredProductsResponse.total_count || 
                                  filteredProductsResponse.page_info?.total || 
                                  filteredProductsResponse.items.length;
      } else if (Array.isArray(filteredProductsResponse)) {
        products.value = filteredProductsResponse;
        totalProductsCount.value = filteredProductsResponse.length;
      } else {
        products.value = filteredProductsResponse || [];
        totalProductsCount.value = products.value.length;
      }
    } else {
      // If no filters, reload category data with current sort and page
      const refreshedCategoryData = await getCategoryData(
        route.params.slug as string, 
        currentPage.value, 
        sort.value
      );
      products.value = refreshedCategoryData?.products?.items || [];
      totalProductsCount.value = refreshedCategoryData?.products?.total_count || products.value.length;
    }
  } catch (error) {
    products.value = [];
    totalProductsCount.value = 0;
  } finally {
    loading.value = false;
  }
}

// Reactive computed for productsCatalog
const productsCatalog = computed(
  () =>
    products.value?.map(({ id, name, sku, url_key, image, price_range, review_count, rating_summary, media_gallery, description, short_description}) => ({
      id,
      name,
      url: `${paths.product}/${url_key}`,
      sku: sku || '',
      price: {"value" : {"amount": price_range?.minimum_price?.final_price.value}},
      primaryImage: image,
      slug: url_key,
      rating: {"count": review_count, "average": rating_summary*5/100},
      gallery: media_gallery ?? [],
      description: short_description?.html || '',
    })) || [],
);

// Watch for route query changes (including sort) and reload products
watch(
  () => route.query,
  async (newQuery, oldQuery) => {
    // Check if sorting changed
    const sortChanged = newQuery.sort !== oldQuery?.sort;
    // Check if pagination changed
    const pageChanged = newQuery.page !== oldQuery?.page;
    // Check if filters changed
    const filterKeys = Object.keys(newQuery).filter(key => 
      key !== 'page' && key !== 'sort' && key !== 'slug'
    );
    const oldFilterKeys = Object.keys(oldQuery || {}).filter(key => 
      key !== 'page' && key !== 'sort' && key !== 'slug'
    );
    const filtersChanged = 
      filterKeys.length !== oldFilterKeys.length ||
      filterKeys.some(key => newQuery[key] !== oldQuery?.[key]);
    // Reload products if any of these changed
    if (sortChanged || pageChanged || filtersChanged) {
      await loadProducts();
    }
  },
  { deep: true }
);

// Initial load - handle URL with existing query parameters
onMounted(async () => {
  // Always load products on mount to handle direct URL access with parameters
  await loadProducts();
  // If there are no initial products and no filters, fallback to category default
  if (products.value.length === 0 && !hasFiltersInUrl.value) {
    products.value = categoryData.value?.products?.items || [];
    totalProductsCount.value = categoryData.value?.products?.total_count || 0;
  }
});
</script>